import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject } from '@angular/core';
import { signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { produce } from 'immer';
import { catchError, filter, map, mergeMap, of, pipe, switchMap, tap } from 'rxjs';

import { IdValueType, KeyValueDescriptionType } from '@models/common/keyValueModel';
import { SearchViewModel } from '@models/module/grid';
import { ActivityRequestParams, ActivityService } from '@module-instance/activity/common';
import { EntityInstanceTransition } from '@module-instance/dynamic-module/common';
import { DetailsStoreService } from '@module-instance/dynamic-module/document-details/common/services/details-store-helper.service';
import { ModuleInstanceService } from '@module-instance/services';
import { ModuleInstanceCommonService } from '@module-instance/services/module-instance-common.service';
import { TableDataService } from '@shared/components/data-list/common/services/table-data.service';
import { DocumentDetailsQueryParams, DocumentDetailsService, DocumentStoreActions, initialState } from '..';

/** This is the type for parameters used in most actions */
type LimitedParams = Pick<
  DocumentDetailsQueryParams,
  'entityInstanceId' | 'contractId' | 'moduleId' | 'paymentPeriodId' | 'moduleTypeId'
>;

/**
 * To use this store, you have some steps to follow:
 * @1 You need to decide which documents you need to load. If you need the header info - load it with `loadHeaderInfo` method. This will also select the first document, and set the params for each one.
 * @1.1 If you don't need the header info, you can select the document with `selectDocument` method, and set the params with `setDocumentParams` method.
 *
 * @2 Load the general info, relations, activities and signers with the respective methods. You can use effects to react on params or selected document changes.
 */
export const DocumentDetailsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('dmStore'),

  withMethods(store => {
    const documentDetailsService = inject(DocumentDetailsService);
    const moduleInstanceCommonService = inject(ModuleInstanceCommonService);
    const moduleInstanceService = inject(ModuleInstanceService);
    const tableDataService = inject(TableDataService);
    const activityService = inject(ActivityService);
    const helperService = inject(DetailsStoreService);

    /** This loads general informations, activity, signers and relations (by need) */
    const loadGeneralInformation = rxMethod<LimitedParams>(
      pipe(
        tap(({ entityInstanceId }) =>
          updateState(store, DocumentStoreActions.LoadGeneralInfo, ({ documents }) => ({
            documents: produce(documents, draft => {
              const doc = draft[entityInstanceId];
              doc.info = null;
              doc.transitionData = null;
              return draft;
            }),
          })),
        ),
        switchMap(params =>
          moduleInstanceCommonService.getGeneralInformation(params).pipe(
            filter(() => !!store.documents()[params.entityInstanceId]), // if the document was closed while loading, do not update the store
            tap(info => {
              updateState(store, DocumentStoreActions.LoadGeneralInfoSuccess, ({ documents, sidebar }) => ({
                documents: produce(documents, draft => {
                  const doc = draft[params.entityInstanceId];
                  doc.info = info;
                  doc.params.workflowId = info.workflowId;
                  doc.actionsDisabled = false;
                  if (!doc.params.screenId && info.screenId) {
                    doc.params.screenId = info.screenId;
                  }
                  return draft;
                }),
                sidebar: produce(sidebar, draft => {
                  const index = draft.items.findIndex(item => item.entity_instance_id === params.entityInstanceId);
                  if (index >= 0) {
                    const { state, stateColor, stateTextColor, documentNumber, moduleDocumentNumber } = info;
                    const colorInfo = tableDataService.mapState({
                      needs_approval: !!info.approvalId,
                      state_color: stateColor,
                      state_text_color: stateTextColor,
                      state_name: state,
                    });
                    draft.items[index] = {
                      ...draft.items[index],
                      state_name: colorInfo,
                      full_doc_num: moduleDocumentNumber ?? documentNumber,
                    };
                  }
                  return draft;
                }),
              }));

              const { isClosedState, approvalId, isDraftState } = info;
              if (isClosedState) {
                loadSigners(params);
              }

              if (!isDraftState || approvalId) {
                loadActivityInstances(params);
                loadRelations(params);
              }
            }),
            filter(info => !info.isDraftState),
            switchMap(() => moduleInstanceService.getAssignmentEntityInstance(params)),
            tap(assignment =>
              updateState(store, DocumentStoreActions.LoadAssignmentSuccess, ({ documents }) => ({
                documents: produce(documents, draft => {
                  draft[params.entityInstanceId].assignment = assignment;
                  return draft;
                }),
              })),
            ),
          ),
        ),
      ),
    );

    const loadRelations = rxMethod<LimitedParams>(
      pipe(
        tap(({ entityInstanceId }) =>
          updateState(store, DocumentStoreActions.LoadRelations, ({ documents }) => ({
            documents: produce(documents, draft => {
              draft[entityInstanceId].relations = null;
              return draft;
            }),
          })),
        ),
        switchMap(({ entityInstanceId, ...params }) =>
          documentDetailsService.getRelations({ entityInstanceId, ...params }).pipe(
            tap(relations =>
              updateState(store, DocumentStoreActions.LoadRelationsSuccess, ({ documents }) => ({
                documents: produce(documents, draft => {
                  draft[entityInstanceId].relations = relations;
                  return draft;
                }),
              })),
            ),
          ),
        ),
      ),
    );

    const loadSigners = rxMethod<LimitedParams>(
      pipe(
        tap(({ entityInstanceId }) =>
          updateState(store, DocumentStoreActions.LoadSigners, ({ documents }) => ({
            documents: produce(documents, draft => {
              draft[entityInstanceId].signers = null;
              return draft;
            }),
          })),
        ),
        switchMap(({ entityInstanceId, ...params }) =>
          moduleInstanceService.getEntityInstanceSigners({ entityInstanceId, ...params }).pipe(
            tap(signers =>
              updateState(store, DocumentStoreActions.LoadSignersSuccess, ({ documents }) => ({
                documents: produce(documents, draft => {
                  draft[entityInstanceId].signers = signers;
                  return draft;
                }),
              })),
            ),
          ),
        ),
      ),
    );

    const loadActivityInstances = rxMethod<LimitedParams>(
      pipe(
        tap(({ entityInstanceId }) =>
          updateState(store, DocumentStoreActions.LoadActivityInstances, ({ documents }) => ({
            documents: produce(documents, draft => {
              draft[entityInstanceId].activityInstances = [];
              return draft;
            }),
          })),
        ),
        switchMap(params =>
          activityService.getActivityList(params).pipe(
            map(activityInstances => activityInstances.map(instance => ({ instance }))),
            tap(activityInstances =>
              updateState(store, DocumentStoreActions.LoadActivityInstancesSuccess, ({ documents }) => ({
                documents: produce(documents, draft => {
                  draft[params.entityInstanceId].activityInstances = activityInstances;
                  return draft;
                }),
              })),
            ),
          ),
        ),
      ),
    );

    return {
      /** @deprecated */
      loadSigners: loadSigners,
      loadRelations,
      loadActivityInstances,
      loadGeneralInformation,

      clearStore: () =>
        updateState(store, DocumentStoreActions.ClearStore, ({ startScreens }) => ({ ...initialState, startScreens })),

      // MARK: Sidebar items
      /** Load The sidebar items when navigating to a document (including opening it from datalist) */
      loadSidebarItems: (
        params: Pick<DocumentDetailsQueryParams, 'contractId' | 'moduleId'> & { page?: number; criteria?: string },
      ) => {
        const { contractId, moduleId, criteria, page } = params;
        const sidebarSearchViewModel = store.sidebar.searchViewModel();
        if (page === 0) {
          updateState(store, DocumentStoreActions.LoadSidebarItems, ({ sidebar }) => ({
            sidebar: {
              ...sidebar,
              items: [],
            },
          }));
        }
        return documentDetailsService
          .gridSidebarSearch({
            body: {
              ...sidebarSearchViewModel,
              page: page + 1,
              criteria: criteria || sidebarSearchViewModel?.criteria || '',
            },
            moduleId,
            contractId,
          })
          .pipe(
            tap(({ data, records, descriptionField }) =>
              updateState(store, DocumentStoreActions.LoadSidebarItemsSuccess, ({ sidebar }) => ({
                sidebar: {
                  ...sidebar,
                  items: sidebar.items.concat(data),
                  descriptionField,
                  records,
                },
              })),
            ),
          );
      },

      setSidebarSearchViewModel: (searchViewModel: SearchViewModel) =>
        updateState(store, DocumentStoreActions.SetSidebarSearchViewModel, ({ sidebar }) => ({
          sidebar: { ...sidebar, searchViewModel },
        })),

      setStartScreens: (startScreens: KeyValueDescriptionType<number>[]) =>
        updateState(store, DocumentStoreActions.SetStartScreens, ({ startScreens: _ }) => ({
          startScreens,
        })),

      // MARK: Activity tab
      loadActivityDetails: rxMethod<ActivityRequestParams>(
        pipe(
          mergeMap(params =>
            activityService.getActivityItem(params).pipe(
              tap(activityDetails =>
                updateState(store, DocumentStoreActions.LoadActivityDetailsSuccess, ({ documents }) => ({
                  documents: produce(documents, draft => {
                    const activityIndex = draft[params.entityInstanceId].activityInstances.findIndex(
                      ({ instance }) => instance.actionInstanceId === params.actionInstanceId,
                    );
                    draft[params.entityInstanceId].activityInstances[activityIndex].activityDetails = activityDetails;
                    return draft;
                  }),
                })),
              ),
            ),
          ),
        ),
      ),

      rollbackLastTransition: rxMethod<
        Pick<ActivityRequestParams, 'entityInstanceId' | 'actionInstanceId' | 'notifyUsers'>
      >(
        pipe(
          map(({ actionInstanceId, entityInstanceId, notifyUsers }) => {
            const { moduleId, contractId } = store.documents()[entityInstanceId].params;
            return {
              actionInstanceId,
              entityInstanceId,
              moduleId,
              contractId,
              notifyUsers,
            };
          }),
          tap(({ entityInstanceId }) =>
            updateState(store, DocumentStoreActions.Rollback, ({ documents }) => ({
              documents: produce(documents, draft => {
                draft[entityInstanceId].loadingRollback = 'loading';
                draft[entityInstanceId].actionsDisabled = true;
                return draft;
              }),
            })),
          ),
          mergeMap(({ actionInstanceId, entityInstanceId, moduleId, contractId, notifyUsers }) =>
            moduleInstanceService
              .rollbackLastTransition({
                actionInstanceId,
                contractId,
                moduleId,
                notifyUsers,
              })
              .pipe(
                tap(() => {
                  updateState(store, DocumentStoreActions.RollbackSuccess, ({ documents }) => ({
                    documents: produce(documents, draft => {
                      draft[entityInstanceId].loadingRollback = 'success';
                      draft[entityInstanceId].actionsDisabled = false;
                      return draft;
                    }),
                    reloadTable: true,
                  }));
                  /* Reload the document details after rollback */
                  loadGeneralInformation({ entityInstanceId, contractId, moduleId });
                }),
                catchError(() => {
                  updateState(store, DocumentStoreActions.RollbackError, ({ documents }) => ({
                    documents: produce(documents, draft => {
                      draft[entityInstanceId].loadingRollback = 'error';
                      draft[entityInstanceId].actionsDisabled = false;
                      return draft;
                    }),
                  }));
                  return of(null);
                }),
              ),
          ),
        ),
      ),

      /**
       * Temporary: clear sticky rollback success so hybrid AngularJS hosts that remount
       * document-details-tabs (e.g. contract details) do not re-emit rollbacktriggered in a loop.
       * Remove once contract details is migrated to Angular.
       */
      clearLoadingRollback: (entityInstanceId: number) =>
        updateState(store, DocumentStoreActions.ClearLoadingRollback, ({ documents }) => ({
          documents: produce(documents, draft => {
            if (draft[entityInstanceId]) {
              draft[entityInstanceId].loadingRollback = null;
            }
            return draft;
          }),
        })),

      /** Set actions disabled state. This disables all the actions that modifies document details */
      setActionsDisabled: (actionsDisabled: boolean, entityInstanceId: number) =>
        updateState(store, DocumentStoreActions.SetActionsDisabled, ({ documents }) => ({
          documents: produce(documents, draft => {
            draft[entityInstanceId].actionsDisabled = actionsDisabled;
            return draft;
          }),
        })),

      setDocumentParams: (params: DocumentDetailsQueryParams) =>
        /* Do not use produce here because documents[entityInstanceId] may be undefined */
        updateState(store, DocumentStoreActions.SetDocumentParams, ({ documents }) => ({
          documents: {
            ...documents,
            [params.entityInstanceId]: {
              ...documents[params.entityInstanceId],
              params,
            },
          },
        })),

      setDocumentAsDirty: (entityInstanceId: number) => {
        if (store.dirtyDocuments().includes(entityInstanceId)) {
          return;
        }
        updateState(store, DocumentStoreActions.SetDocumentAsDirty, ({ dirtyDocuments }) => ({
          dirtyDocuments: [...dirtyDocuments, entityInstanceId],
        }));
      },

      setReloadTable: (reloadTable: boolean) =>
        updateState(store, DocumentStoreActions.SetReloadTable, {
          reloadTable,
        }),

      duplicateDocument: (entityInstanceId: number) => {
        const { moduleId, contractId, screenId, workflowId } = store.documents()[entityInstanceId].params;
        updateState(store, DocumentStoreActions.DuplicateDocument, ({ documents }) => ({
          documents: produce(documents, draft => {
            draft[0] = {
              info: null,
              relations: null,
              signers: null,
              activityInstances: null,
              params: {
                entityInstanceId: 0,
                moduleId,
                contractId,
                screenId,
                workflowId,
              },
              transitionData: null,
              actionsDisabled: true,
              moduleRoles: null,
              loadingRollback: null,
            };
            return draft;
          }),
        }));
      },

      // MARK: Transition methods
      startTransition: (
        transition: EntityInstanceTransition,
        entityInstanceId: number,
        draftValues?: IdValueType<any, number>[],
      ) => {
        /* set loading state */
        updateState(store, DocumentStoreActions.StartDocumentTransition, state =>
          produce(state, draft => {
            const selectedDocument = draft.documents[entityInstanceId];
            selectedDocument.transitionData = {
              role: transition.roles[0],
              transition: transition,
            };
            selectedDocument.actionsDisabled = true;
            return draft;
          }),
        );

        return helperService.startTransition(store.documents(), transition, entityInstanceId, draftValues).pipe(
          map(documents => {
            updateState(store, DocumentStoreActions.StartDocumentTransition, { documents });
            return documents[entityInstanceId].transitionData;
          }),
        );
      },

      cancelTransition: (entityInstanceId: number) =>
        updateState(store, DocumentStoreActions.CancelDocumentTransition, state =>
          produce(state, draft => {
            const selectedDocument = draft.documents[entityInstanceId];
            selectedDocument.transitionData = null;
            selectedDocument.actionsDisabled = false;
            return draft;
          }),
        ),
    };
  }),
);
